import { APP_INITIALIZER, Provider } from '@angular/core';

import { getEffectiveLang } from './lang-pref';

const HAS_LANGUAGE_PREFIX = /^\/(en|de|es|fr|it|pt)(\/|$)/;

export type RedirectLocation = Pick<Location, 'pathname' | 'search' | 'hash' | 'replace'>;

/**
 * Sicherheitsnetz fuer prefix-lose Einstiege (alte Bookmarks, externe Links,
 * sprachlose CTAs, seit v7.4.150 der Teilnehmer-Link `whocan.org/vote/{slug}`).
 * Ohne Prefix greift der Firebase-Catch-all auf den Root-Build — und der ist
 * Englisch. Wer eine Sprache gewaehlt hat oder einen passenden Browser
 * mitbringt, wird hier einmalig auf das Sprach-Aequivalent derselben Route
 * umgeschrieben.
 *
 * Laeuft vor dem Router-Start. Ein `replace` statt `assign`: kein zusaetzlicher
 * History-Eintrag, der Back-Button bleibt brauchbar. In den Sprach-Builds greift
 * die Bedingung nie (der Pfad traegt dort immer ein Prefix), und Googlebot ohne
 * Praeferenz sieht Englisch.
 *
 * Nach dem `replace` haelt das zurueckgegebene, nie aufgeloeste Promise den
 * Bootstrap an: der Root-Build darf weder den Router starten noch Uebersetzungen
 * laden, solange die Navigation laeuft. WebKit bricht die XHRs der alten Seite
 * beim Start der Navigation ab; der Uebersetzungsfehler liess den Router mit
 * `history.replaceState('/en/')` zurueckrollen, und das ersetzte die laufende
 * Navigation durch die Website-Startseite `/en/` (Hotfix 2026-10-30,
 * `_whocan-info/product/report-vote-link-safari-startpage-hotfix-2026-10-30.md`).
 */
export function redirectToPreferredLanguage(location: RedirectLocation | undefined = typeof window === 'undefined' ? undefined : window.location): Promise<never> | void {
    if (!location) {
        return;
    }
    const pathname = location.pathname;
    if (HAS_LANGUAGE_PREFIX.test(pathname)) {
        return;
    }
    const lang = getEffectiveLang();
    if (lang === 'en') {
        // Der Root-Build IST der englische — nichts zu tun.
        return;
    }
    location.replace(`/${lang}${pathname}${location.search}${location.hash}`);
    return new Promise<never>(() => undefined);
}

export const LANGUAGE_REDIRECT_INITIALIZER: Provider = {
    provide: APP_INITIALIZER,
    useValue: () => redirectToPreferredLanguage(),
    multi: true,
};
